// Keep one accepted response for both avatar rows. Livewire is resolved afresh
// by its ID so delayed searches never retain an element-bound $wire proxy.
export default (config) => {
  const wireId = config.componentId
  let sequence = 0
  let timer = null
  let focusTimer = null
  let alive = true
  let canonicalSelected = [...config.preview.selected]
  const panel = () => Array.from(document.querySelectorAll('[data-test="tasks.assignee-editor"][data-component-id]'))
    .find(element => element.dataset.componentId === wireId)

  return {
    inline: config.preview.inline,
    total: config.preview.total,
    selected: config.preview.selected,
    lastPreview: config.preview,
    options: [],
    search: '',
    page: 1,
    lastPage: 1,
    open: false,
    loading: false,
    selecting: false,
    error: false,
    panelStyle: '',

    async openPicker() {
      if (this.selecting) return
      this.open = true
      this.search = ''
      this.focusWhenReady()
      const rect = this.$refs.more?.getBoundingClientRect()
      if (rect) {
        const width = Math.min(360, window.innerWidth - 16)
        const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))
        this.panelStyle = `left:${left}px;top:${rect.bottom + 8}px;width:${width}px;max-height:calc(100dvh - ${rect.bottom + 24}px)`
      }
      await this.load('', 1, true)
    },

    closePicker(focusTrigger = true) {
      if (!this.open) return
      ++sequence
      clearTimeout(timer)
      clearTimeout(focusTimer)
      this.open = false
      this.loading = false
      if (!this.selecting) this.inline = this.lastPreview.inline
      if (focusTrigger) this.$refs.more?.focus()
    },

    focusWhenReady(attempt = 0) {
      if (!alive || !this.open) return
      const search = panel()?.querySelector('input[type="search"]')
      if (search?.getClientRects().length) {
        search.focus()
        if (document.activeElement === search) return
      }
      if (attempt < 8) {
        focusTimer = setTimeout(() => this.focusWhenReady(attempt + 1), 25)
      }
    },

    handleEscape(event) {
      if (!this.open) return
      event.preventDefault()
      event.stopPropagation()
      this.closePicker()
    },

    trapFocus(event) {
      if (!this.open) return
      const controls = Array.from(panel()?.querySelectorAll('input, button, select, textarea, a[href], [tabindex]') || [])
        .filter(element => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length)
      const first = controls[0]
      const last = controls[controls.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    },

    async load(search, page, refreshInline = false) {
      const requestId = ++sequence
      this.loading = true
      this.error = false
      this.options = []
      try {
        const wire = window.Livewire?.find(wireId)
        if (!wire) throw new Error('Task board is unavailable')
        const response = await wire.refreshTaskAssigneeOptions(search, page, refreshInline ? 'refresh' : 'window', requestId)
        if (!alive || requestId !== sequence || !this.open) return
        if (!Array.isArray(response?.options) || !Array.isArray(response?.inline)) {
          throw new Error('Task options are unavailable')
        }
        this.lastPreview = response
        this.acceptSelection(response)
        this.inline = response.inline
        this.options = response.options
        this.total = response.total
        this.page = response.page
        this.lastPage = response.lastPage
      } catch {
        if (!alive || requestId !== sequence || !this.open) return
        this.error = true
        this.inline = []
        this.options = []
        this.total = 0
        this.lastPreview = { ...this.lastPreview, inline: [], total: 0 }
      } finally {
        if (alive && requestId === sequence) this.loading = false
      }
    },

    scheduleSearch() {
      clearTimeout(timer)
      ++sequence
      this.loading = true
      this.options = []
      const search = this.search
      timer = setTimeout(() => { if (alive && this.open) this.load(search, 1) }, 250)
    },

    async toggle(id, event = null) {
      if (this.loading || this.selecting) {
        if (event?.currentTarget?.type === 'checkbox') event.currentTarget.checked = this.selected.includes(id)
        return
      }
      const trigger = event?.currentTarget
      const next = id === null
        ? []
        : (this.selected.includes(id) ? this.selected.filter(value => value !== id) : [...this.selected, id])
      canonicalSelected = [...this.selected]
      this.selected = next
      this.selecting = true
      this.error = false
      try {
        const wire = window.Livewire?.find(wireId)
        if (!wire) throw new Error('Task board is unavailable')
        const selected = await wire.applyTaskAssigneeFilter(next)
        if (!Array.isArray(selected)) throw new Error('Task selection is unavailable')
        if (alive) {
          canonicalSelected = selected
          this.selected = [...canonicalSelected]
        }
      } catch {
        if (alive) {
          this.selected = [...canonicalSelected]
          this.error = true
        }
      } finally {
        if (alive) {
          this.selecting = false
          if (!this.open) this.inline = this.lastPreview.inline
        }
        if (event?.detail > 0) trigger?.blur()
      }
    },

    acceptSelection(preview) {
      canonicalSelected = [...preview.selected]
      // Echoes refresh the rollback baseline. Only the apply answer or rejection
      // reconciles a pending optimistic selection.
      if (!this.selecting) this.selected = [...canonicalSelected]
    },

    sync(event) {
      const detail = event.detail
      if (!alive || detail?.componentId !== wireId) return
      if (Number.isInteger(detail.requestId) && detail.requestId !== sequence) return
      if (this.loading) return
      this.lastPreview = detail.preview
      this.acceptSelection(detail.preview)
      this.total = detail.preview.total
      if (!this.open) {
        if (!this.selecting) this.inline = detail.preview.inline
      } else {
        const allowed = new Set(detail.allowedIds)
        this.inline = this.inline.filter(entry => allowed.has(entry.id))
        this.options = this.options.filter(entry => allowed.has(entry.id))
      }
    },

    destroy() {
      alive = false
      ++sequence
      clearTimeout(timer)
      clearTimeout(focusTimer)
    }
  }
}
